import { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import {
  combineStyles,
  errorNotificationMapObject,
  postCodeValidator,
} from 'helpers/helpers';
import { fetchUser, clearUser } from 'actions/Users';
import { post, put, get } from 'helpers/apiHelpers';
import FormTextInput from 'components/FormTextInput/FormTextInput';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import FormControlButtons from 'components/FormControlButtons/FormControlButtons';
import FormLabel from '@material-ui/core/FormLabel';
import { Divider, withStyles } from '@material-ui/core';
import SelectInput from 'components/FormSelect/SelectInput';
import _set from 'lodash/set';
import { ReactPhoneNumberAdapter } from 'components/ReactPhoneNumberAdapter/ReactPhoneNumberAdapter';
import {
  fetchSexes,
  fetchHowKnow,
  fetchSmsTagsForClients,
} from 'actions/Dictionary';
import Datetime from 'react-datetime';
import moment from 'moment';
import FormControlStickyButton from 'components/FormControlStickyButton/FormControlStickyButton';
import { toast } from 'react-toastify';
import LabeledCheckbox from 'components/Checkbox/LabeledCheckbox';
import buttonsStyle from 'assets/jss/material-dashboard-pro-react/views/buttonsStyle.jsx';
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';

import LogView from '../../../../components/History/LogView';
import RecordFieldHistory from '../../../../components/History/RecordFieldHistory';
import defaultState from './defaultState';

class GeneralInfo extends Component {
  state = defaultState;

  userId = this.props.match.params.id;

  isEdit = this.props.location.pathname.includes('edit');

  async componentDidMount() {
    const { 'hydra:member': brandTerms } = await get('/brand-terms');

    const emailConsent = brandTerms.find(term => term.type === 'EMAIL');
    const smsConsent = brandTerms.find(term => term.type === 'SMS');
    const phoneConsent = brandTerms.find(term => term.type === 'PHONE');

    this.setState({
      isEmailConsentActive: emailConsent?.active || false,
      isSmsConsentActive: smsConsent?.active || false,
      isPhoneConsentActive: phoneConsent?.active || false,
    });

    await Promise.all([
      this.props.fetchSexes(),
      this.props.fetchHowKnow(),
      this.props.fetchSmsTagsForClients(),
    ]);

    const { dictionary } = this.props;

    let user = {
      ...defaultState,
      sexOptions: dictionary.sexes,
      knowAboutUsOptions: dictionary.howKnow,
    };
    if (this.userId) {
      const givenUser = await this.props.fetchUser(this.userId);
      let knowAboutUs;
      let knowAboutUsOther;

      if (
        dictionary.howKnow.some(
          option => option.value === givenUser.knowAboutUs
        )
      ) {
        knowAboutUs = givenUser.knowAboutUs;
      } else {
        knowAboutUs = this.props.t('clients.fromYouKnow.other.label');
        knowAboutUsOther = givenUser.knowAboutUs;
      }

      user = {
        firstName: givenUser.firstName,
        lastName: givenUser.lastName,
        phone: givenUser.phone,
        email: givenUser.email,
        gender: givenUser.gender?.['@id'], // iri
        sexOptions: dictionary.sexes,
        hasPhoneNumber: !!givenUser?.phone?.number,
        invoiceCompany: givenUser.invoiceCompany,
        invoiceVatNumber: givenUser.invoiceVatNumber,
        invoiceAddressCity: givenUser.invoiceAddressCity,
        invoiceAddressBuildNumber: givenUser.invoiceAddressBuildNumber,
        invoiceAddressPlaceNumber: givenUser.invoiceAddressPlaceNumber,
        invoiceAddressPostCode: givenUser.invoiceAddressPostCode,
        invoiceAddressStreet: givenUser.invoiceAddressStreet,
        invoiceComments: givenUser.invoiceComments,
        referenceCode: givenUser.referenceCode,
        knowAboutUs,
        knowAboutUsOptions: dictionary.howKnow,
        knowAboutUsOther,
        smsConsent: givenUser.smsConsent,
        emailConsent: givenUser.emailConsent,
        phoneConsent: givenUser.phoneConsent,
        messagesTerms: givenUser.messagesTerms,
        smsClientTags: givenUser.smsClientTags,
        birthday: givenUser?.birthday ?? null,
      };
      this.props.getUserFullName(
        `${givenUser.firstName} ${givenUser.lastName}`
      );
    }

    this.setState({
      ...user,
      fetching: false,
    });
  }

  handleChange = event => {
    this.setState({ [event.target.name]: event.target.value });
  };

  handleInputChange = event => {
    this.setState(state => _set(state, event.target.name, event.target.value));
  };

  handlePhoneChange = (number, countryCode) => {
    this.setState(prevState => ({
      ...prevState,
      phone: {
        ...prevState.phone,
        number,
        countryCode,
      },
    }));
  };

  validatePassword = () =>
    this.state.plainPasswordMatch === this.state.plainPassword;

  validateEmail = () => this.state.email.includes('@');

  validateForm = () => {
    if (!this.validateEmail()) {
      toast.error(this.props.t('brands.newBrandForm.helpText.incorrect'));
      return false;
    }

    if (!this.validatePassword()) {
      toast.error(this.props.t('resetPassword.passwordsDontMatch'));
      return false;
    }

    if (this.isEdit) {
      if (!this.state.firstName || !this.state.lastName || !this.state.email) {
        toast.error(this.props.t('errors.fillAllRequiredFields'));
        return false;
      }
    } else if (
      !this.state.firstName ||
      !this.state.lastName ||
      !this.state.email ||
      !this.state.plainPassword ||
      !this.validatePassword()
    ) {
      toast.error(this.props.t('errors.fillAllRequiredFields'));
      return false;
    }

    return true;
  };

  handleSubmit = () => {
    if (!this.validateForm()) {
      return;
    }

    this.setState({
      loading: true,
    });

    const data = {
      firstName: this.state.firstName,
      lastName: this.state.lastName,
      phone:
        !this.state.hasPhoneNumber && !this.state.phone?.number
          ? null
          : this.state.phone,
      email: this.state.email.toLowerCase(),
      plainPassword:
        // eslint-disable-next-line no-negated-condition -- FIXME
        this.state.plainPassword !== '' ? this.state.plainPassword : null,
      gender: this.state.gender || null,
      invoiceVatNumber: this.state.invoiceVatNumber,
      invoiceCompany: this.state.invoiceCompany,
      invoiceAddressCity: this.state.invoiceAddressCity,
      invoiceAddressBuildNumber: this.state.invoiceAddressBuildNumber,
      invoiceAddressPlaceNumber: this.state.invoiceAddressPlaceNumber,
      invoiceAddressPostCode: this.state.invoiceAddressPostCode,
      invoiceAddressStreet: this.state.invoiceAddressStreet,
      invoiceComments: this.state.invoiceComments,
      knowAboutUs:
        this.state.knowAboutUs ===
        this.props.t('clients.fromYouKnow.other.label')
          ? this.state.knowAboutUsOther
          : this.state.knowAboutUs,
      smsConsent: this.state.smsConsent,
      emailConsent: this.state.emailConsent,
      phoneConsent: this.state.phoneConsent,
      messagesTerms: this.state.messagesTerms,
      smsClientTags: this.state.smsClientTags.map(el => el?.['@id'] ?? el),
      birthday: this.state.birthday,
    };

    const action = this.isEdit
      ? put(`/clients/${this.userId}`, data)
      : post('/clients', data);

    // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
    action.then(
      newUser => {
        this.setState({
          loading: false,
        });

        if (this.isEdit) {
          return this.props.history.push('/admin/clients');
        }
        toast.success(this.props.t('generalInfo.createdClient.succes.info'));

        if (typeof this.props.onAfterSubmit === 'function') {
          return this.props.onAfterSubmit(newUser);
        }

        return this.props.history.push(
          `/admin/clients/edit/${newUser?.id}?openAddresses`
        );
      },
      // eslint-disable-next-line promise/prefer-await-to-callbacks -- FIXME
      error => {
        const { propertyPath, message } =
          // eslint-disable-next-line no-unsafe-optional-chaining -- FIXME
          error?.response?.data?.violations?.[0];
        if (propertyPath === 'email' || propertyPath === 'plainPassword') {
          this.setState({
            loading: false,
          });
          return toast.error(
            `${errorNotificationMapObject[propertyPath]}: ${message}`
          );
        }
        if (propertyPath === 'invoiceVatNumber') {
          this.setState({
            loading: false,
          });
          return toast.error(message);
        }
        this.setState({
          loading: false,
        });
        return toast.error(message);
      }
    );
  };

  render() {
    const { classes, addNewUserOnNewOrderForm = false } = this.props;
    const {
      firstName,
      lastName,
      phone,
      email,
      plainPassword,
      plainPasswordMatch,
      gender,
      sexOptions,
      invoiceCompany,
      invoiceVatNumber,
      invoiceAddressCity,
      invoiceAddressBuildNumber,
      invoiceAddressPlaceNumber,
      invoiceAddressPostCode,
      invoiceAddressStreet,
      invoiceComments,
      referenceCode: givenReferenceCode,
      knowAboutUsOptions,
      knowAboutUs,
      knowAboutUsOther,
    } = this.state;

    const { t } = this.props;
    const referenceCode =
      givenReferenceCode === null ? '' : givenReferenceCode.code;

    const isAnyConsentActive =
      this.state.isEmailConsentActive ||
      this.state.isSmsConsentActive ||
      this.state.isPhoneConsentActive;

    return (
      <form
        style={{ opacity: this.state.fetching ? 0 : 1, transition: '0.5s all' }}
      >
        <Card>
          <CardBody>
            <GridContainer>
              <GridItem sm={4}>
                <h4>{this.props.t('clients.basicInformation')}</h4>
                <FormTextInput
                  label={`${this.props.t('clients.name')} *`}
                  classes={classes}
                  name="firstName"
                  value={firstName}
                  handleChange={this.handleInputChange}
                  inputSize={12}
                  maxLength={64}
                />
                <FormTextInput
                  label={`${this.props.t('clients.surname')} *`}
                  classes={classes}
                  name="lastName"
                  value={lastName}
                  handleChange={this.handleInputChange}
                  inputSize={12}
                  maxLength={64}
                />
                <ReactPhoneNumberAdapter
                  label={this.props.t('clients.phone')}
                  name="phone"
                  value={phone}
                  onChange={this.handlePhoneChange}
                  inputSize={12}
                />
                <SelectInput
                  dataCy="select__gender"
                  classes={classes}
                  label={this.props.t('clients.gender')}
                  options={sexOptions}
                  value={gender}
                  mapBy="value"
                  trackBy="@id"
                  name="gender"
                  handleChange={this.handleInputChange}
                  id="gender"
                  size={12}
                />
                <FormLabel
                  className={classes.labelHorizontal}
                  style={{ marginBottom: '5px' }}
                >
                  {this.props.t('clients.birthdayDate', 'Data urodzenia')}
                </FormLabel>
                <Datetime
                  dateFormat={moment.localeData().longDateFormat('L')}
                  timeFormat={false}
                  // eslint-disable-next-line new-cap -- FIXME
                  value={new moment(this.state.birthday)}
                  onChange={e =>
                    this.setState({ birthday: e.format('YYYY-MM-DD') })
                  }
                  inputProps={{
                    readOnly: true,
                  }}
                  locale="pl"
                />
                <FormTextInput
                  label={this.props.t('clients.refCode')}
                  style={{ marginTop: '2px' }}
                  classes={classes}
                  name="email"
                  value={referenceCode}
                  handleChange={this.handleInputChange}
                  inputSize={12}
                  disabled={true}
                />
                <SelectInput
                  classes={classes}
                  label={this.props.t('clients.fromYouKnow')}
                  options={[
                    ...knowAboutUsOptions,
                    { value: t('clients.fromYouKnow.other.label') },
                  ]}
                  value={knowAboutUs}
                  mapBy="value"
                  trackBy="value"
                  name="knowAboutUs"
                  handleChange={this.handleInputChange}
                  id="knowAboutUs"
                  size={12}
                  dataCy="select__knowAboutUs"
                />
                {knowAboutUs === t('clients.fromYouKnow.other.label') && (
                  <FormTextInput
                    disabled={
                      knowAboutUs !== t('clients.fromYouKnow.other.label')
                    }
                    multiline={true}
                    label={t('clients.fromYouKnow.other.label')}
                    classes={classes}
                    name="knowAboutUsOther"
                    value={knowAboutUsOther}
                    handleChange={this.handleInputChange}
                    inputSize={12}
                    placeholder={t('clients.fromYouKnow.other.placeholder')}
                  />
                )}
                <FormTextInput
                  label={`${this.props.t('clients.emailAddress')} *`}
                  classes={classes}
                  name="email"
                  value={email}
                  noAutoComplete={true}
                  handleChange={this.handleInputChange}
                  inputSize={12}
                />
                <FormTextInput
                  label={
                    this.isEdit
                      ? this.props.t('clients.newPassword')
                      : `${this.props.t('clients.password')} *`
                  }
                  classes={classes}
                  name="plainPassword"
                  value={plainPassword}
                  type="password"
                  noAutoComplete={true}
                  handleChange={this.handleInputChange}
                  inputSize={12}
                />
                <FormTextInput
                  label={
                    this.isEdit
                      ? this.props.t('clients.repeatNewPassword')
                      : `${this.props.t('clients.repeatPassword')} *`
                  }
                  classes={classes}
                  name="plainPasswordMatch"
                  value={plainPasswordMatch}
                  success={
                    this.state.plainPassword !== '' &&
                    this.state.plainPassword === this.state.plainPasswordMatch
                  }
                  error={
                    !(
                      this.state.plainPassword === this.state.plainPasswordMatch
                    )
                  }
                  type="password"
                  handleChange={this.handleInputChange}
                  inputSize={12}
                />
              </GridItem>
              <GridItem sm={4}>
                <h4>{this.props.t('clients.invoiceData')}</h4>
                <FormTextInput
                  label={this.props.t('clients.companyName')}
                  classes={classes}
                  name="invoiceCompany"
                  value={invoiceCompany}
                  handleChange={this.handleInputChange}
                  inputSize={12}
                />
                <FormTextInput
                  label={this.props.t('clients.taxId')}
                  classes={classes}
                  name="invoiceVatNumber"
                  value={invoiceVatNumber}
                  handleChange={this.handleInputChange}
                  inputSize={12}
                />
                <FormTextInput
                  label={this.props.t('common.address.city')}
                  classes={classes}
                  name="invoiceAddressCity"
                  value={invoiceAddressCity}
                  handleChange={this.handleInputChange}
                  inputSize={12}
                />
                <FormTextInput
                  label={this.props.t('common.address.postCode')}
                  classes={classes}
                  error={!postCodeValidator(invoiceAddressPostCode)}
                  success={postCodeValidator(invoiceAddressPostCode)}
                  value={invoiceAddressPostCode}
                  onChange={this.handleInputChange}
                  name="invoiceAddressPostCode"
                  inputSize={12}
                />
                <FormTextInput
                  label={this.props.t('common.address.street')}
                  classes={classes}
                  name="invoiceAddressStreet"
                  value={invoiceAddressStreet}
                  handleChange={this.handleInputChange}
                  inputSize={12}
                />
                <FormTextInput
                  label={this.props.t('common.address.buildingNo')}
                  classes={classes}
                  name="invoiceAddressBuildNumber"
                  value={invoiceAddressBuildNumber}
                  handleChange={this.handleInputChange}
                  inputSize={12}
                />
                <FormTextInput
                  label={this.props.t('common.address.localNo')}
                  classes={classes}
                  name="invoiceAddressPlaceNumber"
                  value={invoiceAddressPlaceNumber}
                  handleChange={this.handleInputChange}
                  inputSize={12}
                />
                <FormTextInput
                  style={{ marginTop: '6px' }}
                  label={this.props.t('clients.notesToInvoice')}
                  classes={classes}
                  name="invoiceComments"
                  value={invoiceComments}
                  handleChange={this.handleInputChange}
                  inputSize={12}
                />
              </GridItem>
              <GridItem sm={4}>
                <h4>{this.props.t('clients.userAgree')}</h4>
                <FormLabel
                  style={{ marginTop: '10px' }}
                  className={classes.labelHorizontal}
                >
                  {this.props.t('clients.marketingConsents')}
                </FormLabel>
                <Divider style={{ marginBottom: '15px' }} />
                {isAnyConsentActive && (
                  <div className={classes.marketingConsentsWrapper}>
                    {this.state.isEmailConsentActive && (
                      <LabeledCheckbox
                        checked={this.state.emailConsent}
                        onClick={e => {
                          this.setState({
                            emailConsent: e.target.checked,
                          });
                        }}
                        label={this.props.t('clients.emailConsent')}
                        className={classes.labeledCheckboxBlock}
                      />
                    )}
                    {this.state.isSmsConsentActive && (
                      <LabeledCheckbox
                        checked={this.state.smsConsent}
                        onClick={e => {
                          this.setState({
                            smsConsent: e.target.checked,
                          });
                        }}
                        label={this.props.t('clients.smsConsent')}
                        className={classes.labeledCheckboxBlock}
                      />
                    )}
                    {this.state.isPhoneConsentActive && (
                      <LabeledCheckbox
                        checked={this.state.phoneConsent}
                        onClick={e => {
                          this.setState({
                            phoneConsent: e.target.checked,
                          });
                        }}
                        label={this.props.t('clients.phoneConsent')}
                        className={classes.labeledCheckboxBlock}
                      />
                    )}
                  </div>
                )}
                <FormLabel
                  style={{ marginTop: '20px' }}
                  className={classes.labelHorizontal}
                >
                  {this.props.t('clients.messagesTerms')}
                </FormLabel>
                <Divider style={{ marginBottom: '15px' }} />
                <LabeledCheckbox
                  checked={this.state.messagesTerms}
                  onChange={e => {
                    this.setState({
                      messagesTerms: e.target.checked,
                    });
                  }}
                  label={
                    this.state.messagesTermsContent
                      ? this.state.messagesTermsContent
                      : this.props.t('clients.messagesTerms')
                  }
                  className={classes.labeledCheckboxBlock}
                />
                <FormLabel
                  style={{ marginTop: '20px' }}
                  className={classes.labelHorizontal}
                >
                  {this.props.t('clients.smsClientTags', 'Tagi sms')}
                </FormLabel>
                <Divider style={{ marginBottom: '15px' }} />
                <SelectInput
                  noGrid={true}
                  classes={classes}
                  mapBy="value"
                  trackBy="@id"
                  name="smsClientTags"
                  multiple={true}
                  value={this.state.smsClientTags}
                  options={this.props.smsClientTags}
                  disabled={false}
                  handleChange={event => {
                    this.handleChange(event);
                  }}
                />
              </GridItem>
            </GridContainer>
          </CardBody>

          <CardBody>
            {this.userId && (
              <>
                <RecordFieldHistory
                  iri={`/clients/${this.userId}`}
                  defaultField="email"
                  fields={[
                    {
                      name: this.props.t('clients.emailAddress'),
                      val: 'email',
                    },
                    {
                      name: this.props.t('clients.phone'),
                      val: 'phone.number',
                    },
                    {
                      name: this.props.t('clients.options.emailConsent'),
                      val: 'emailConsent',
                    },
                    {
                      name: this.props.t('clients.options.smsConsent'),
                      val: 'smsConsent',
                    },
                    {
                      name: this.props.t('clients.options.phoneConsent'),
                      val: 'phoneConsent',
                    },
                  ]}
                />
              </>
            )}
          </CardBody>
        </Card>
        {addNewUserOnNewOrderForm && (
          <FormControlButtons
            t={this.props.t}
            classes={classes}
            discardText={this.props.t('form.cancel')}
            submitText={this.props.t('form.save')}
            cancelPath={this.props.cancelPath}
            handleSubmit={this.handleSubmit}
            history={false}
            customOffsetSmall="13px"
            customOffsetLarge="13px"
            isFixedToBottom={true}
          />
        )}
        {!this.isEdit && !addNewUserOnNewOrderForm && (
          <FormControlStickyButton
            t={this.props.t}
            classes={classes}
            discardText={this.props.t('form.cancel')}
            submitText={this.props.t('form.save')}
            cancelPath={this.props.cancelPath}
            handleSubmit={this.handleSubmit}
            history={false}
            customOffsetSmall="13px"
            customOffsetLarge="13px"
            isFixedToBottom={true}
          />
        )}
        {this.isEdit && (
          <LogView
            t={this.props.t}
            classes={classes}
            discardText={this.props.t('form.cancel')}
            submitText={this.props.t('form.save')}
            cancelPath={this.props.cancelPath}
            handleSubmit={this.handleSubmit}
            iri={`/clients/${this.userId}`}
            customOffsetSmall="43px"
            customOffsetLarge="43px"
          />
        )}
      </form>
    );
  }
}

const styles = {
  marketingConsentsWrapper: {
    '& > label:not(:last-child)': {
      marginBottom: '15px',
    },
  },
  labeledCheckboxBlock: {
    display: 'flex',
  },
};
const combinedStyles = combineStyles(extendedFormsStyle, buttonsStyle, styles);

const mapStateToProps = state => ({
  user: state.Users.user,
  currentBrand: state.Brands.brand,
  selectedRegionForValidations: state.Brands.selectedRegionForValidations,
  dictionary: state.Dictionary,
  smsClientTags: state.Dictionary.smsClientTags,
});

const mapDispatchToProps = {
  fetchUser,
  clearUser,
  fetchSexes,
  fetchHowKnow,
  fetchSmsTagsForClients,
};

const enhance = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withRouter,
  withTranslation(),
  withStyles(combinedStyles)
);

export default enhance(GeneralInfo);
